import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Calculator, Save, FileText, ArrowRight, Check, Trash2, ChevronDown, ChevronUp, Upload, HelpCircle, Filter, Clock, User, SortAsc } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { FY_TABLE, ALL_FYS, calculateInternal, buildFilingMatrix, fmt } from '@/lib/ccfsCalcEngine';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CESSATION_OPTIONS = [
  'FY 2017-18', 'FY 2018-19', 'FY 2019-20', 'FY 2020-21',
  'FY 2021-22', 'FY 2022-23', 'FY 2023-24', 'FY 2024-25', 'FY 2025-26',
];

const LAST_FILED_OPTIONS = [
  'FY 2016-17', 'FY 2017-18', 'FY 2018-19', 'FY 2019-20',
  'FY 2020-21', 'FY 2021-22', 'FY 2022-23', 'FY 2023-24',
];

export default function InternalCalculator({ token, recalcLead, isAdmin = false }) {
  // Worksheet management
  const [worksheets, setWorksheets] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newCompany, setNewCompany] = useState('');
  const [showList, setShowList] = useState(true);

  // Admin list view filters
  const [sourceFilter, setSourceFilter] = useState('all');
  const [ageingFilter, setAgeingFilter] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [collapsedGroups, setCollapsedGroups] = useState({});

  // Calculator state (mirrors worksheet fields)
  const [companyName, setCompanyName] = useState('');
  const [statusField, setStatusField] = useState('');
  const [cessationFY, setCessationFY] = useState('');
  const [intent, setIntent] = useState('');
  const [lastFiledFY, setLastFiledFY] = useState('');
  const [filingMatrix, setFilingMatrix] = useState({});
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showCinModal, setShowCinModal] = useState(false);
  const [cinValue, setCinValue] = useState('');
  const [cinError, setCinError] = useState('');
  const navigate = useNavigate();
  const [savedToLead, setSavedToLead] = useState(false);
  const [lastFiledMode, setLastFiledMode] = useState('');

  // Refs for auto-scroll
  const q2Ref = useRef(null);
  const q3Ref = useRef(null);
  const calcBtnRef = useRef(null);
  const cessationRef = useRef(null);
  const resultRef = useRef(null);

  // "Not sure" logic — derive assumed filing FY
  const isNotSure = lastFiledMode === 'noidea';
  const assumedFilingFY = useMemo(() => {
    if (!isNotSure) return null;
    if (statusField === 'closed' && cessationFY) {
      const cessYearStr = cessationFY.replace('FY ', '').split('-')[0];
      const cessYear = parseInt(cessYearStr, 10);
      return `FY ${cessYear - 1}-${String(cessYear).slice(2)}`;
    }
    if (statusField === 'running') {
      const currentYear = new Date().getFullYear();
      const twoYearsAgo = currentYear - 2;
      return `FY ${twoYearsAgo - 1}-${String(twoYearsAgo).slice(2)}`;
    }
    return null;
  }, [isNotSure, statusField, cessationFY]);

  const effectiveLastFiledFY = isNotSure ? (assumedFilingFY || '') : lastFiledFY;

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  // Load worksheets
  const loadWorksheets = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/worksheets`, { headers });
      if (res.ok) setWorksheets(await res.json());
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadWorksheets(); }, [loadWorksheets]);

  // Pre-fill company name when in recalc mode
  useEffect(() => {
    if (recalcLead?.company_name) setNewCompany(recalcLead.company_name);
  }, [recalcLead]);

  // Create new worksheet
  const createWorksheet = async () => {
    if (!newCompany.trim()) { toast.error('Enter company name'); return; }
    try {
      const res = await fetch(`${API_URL}/api/worksheets`, {
        method: 'POST', headers, body: JSON.stringify({ company_name: newCompany.trim() })
      });
      if (res.ok) {
        const ws = await res.json();
        setWorksheets(prev => [ws, ...prev]);
        openWorksheet(ws);
        setNewCompany('');
        toast.success('Worksheet created');
      }
    } catch { toast.error('Failed to create worksheet'); }
  };

  // Open worksheet
  const openWorksheet = (ws) => {
    setActiveWs(ws);
    setCompanyName(ws.company_name || '');
    setStatusField(ws.status_field || '');
    setCessationFY(ws.cessation_fy || '');
    setIntent(ws.intent || '');
    setLastFiledFY(ws.last_filed_fy || '');
    setFilingMatrix(ws.filing_matrix || {});
    setResult(ws.calculation_result || null);
    setShowList(false);
  };

  // Auto-save a field
  const autoSave = async (field, value) => {
    if (!activeWs) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/worksheets/${activeWs.id}`, {
        method: 'PATCH', headers, body: JSON.stringify({ [field]: value })
      });
      if (res.ok) {
        const updated = await res.json();
        setActiveWs(updated);
      }
    } catch { /* silently fail */ }
    finally { setSaving(false); }
  };

  // Handle status change
  const handleStatus = (val) => {
    setStatusField(val);
    if (val === 'running') {
      setCessationFY('');
      // Auto-scroll to Q2 after a tick
      setTimeout(() => q2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    }
    if (val === 'closed') {
      // Auto-scroll to cessation options
      setTimeout(() => cessationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    }
    setResult(null);
    autoSave('status_field', val);
  };

  // Handle cessation FY — rebuild matrix if lastFiledFY already set
  const handleCessation = (val) => {
    setCessationFY(val);
    setResult(null);
    autoSave('cessation_fy', val);
    // Auto-scroll to Q2 (intent)
    setTimeout(() => q2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    if (effectiveLastFiledFY) {
      const lfFY = isNotSure ? ((() => { const y = parseInt(val.replace('FY ', '').split('-')[0], 10); return `FY ${y-1}-${String(y).slice(2)}`; })()) : lastFiledFY;
      const newMatrix = buildFilingMatrix({ lastFiledFY: lfFY, intent, status: statusField, cessationFY: val });
      setFilingMatrix(newMatrix);
      autoSave('filing_matrix', newMatrix);
    }
  };

  // Handle intent change — rebuild matrix if last_filed_fy is set
  const handleIntent = (val) => {
    setIntent(val);
    setResult(null);
    autoSave('intent', val);
    // Auto-scroll to Q3
    setTimeout(() => q3Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 200);
    // If lastFiledFY already set, rebuild matrix with new intent
    if (effectiveLastFiledFY) {
      const newMatrix = buildFilingMatrix({ lastFiledFY, intent: val, status: statusField, cessationFY });
      // Preserve any manual overrides from existing matrix
      const merged = { ...newMatrix };
      for (const fy of Object.keys(filingMatrix)) {
        if (merged[fy]) {
          merged[fy] = filingMatrix[fy];
        }
      }
      setFilingMatrix(merged);
      autoSave('filing_matrix', merged);
    }
  };

  // Handle last filed year change — build initial matrix
  const handleLastFiled = (val) => {
    setLastFiledFY(val);
    setLastFiledMode('select');
    setResult(null);
    autoSave('last_filed_fy', val);
    const newMatrix = buildFilingMatrix({ lastFiledFY: val, intent, status: statusField, cessationFY });
    setFilingMatrix(newMatrix);
    autoSave('filing_matrix', newMatrix);
    // Auto-scroll to matrix/calculate area
    setTimeout(() => calcBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
  };

  // Handle "Not sure" selection for last filed year
  const handleNotSure = () => {
    setLastFiledMode('noidea');
    setLastFiledFY('');
    setResult(null);
    autoSave('last_filed_fy', 'noidea');
  };

  // Toggle individual matrix cell
  const toggleCell = (fy, form) => {
    const updated = { ...filingMatrix, [fy]: { ...filingMatrix[fy], [form]: !filingMatrix[fy]?.[form] } };
    setFilingMatrix(updated);
    setResult(null);
    autoSave('filing_matrix', updated);
  };

  // When "Not sure" mode activates and we have an assumed FY, auto-build the matrix
  useEffect(() => {
    if (isNotSure && assumedFilingFY && intent && statusField) {
      const newMatrix = buildFilingMatrix({ lastFiledFY: assumedFilingFY, intent, status: statusField, cessationFY });
      setFilingMatrix(newMatrix);
      autoSave('filing_matrix', newMatrix);
      setTimeout(() => calcBtnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300);
    }
  }, [isNotSure, assumedFilingFY, intent, statusField, cessationFY]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh calculation if date changes (page kept open past midnight)
  useEffect(() => {
    if (!result || result.empty) return;
    const check = setInterval(() => {
      const now = new Date().toDateString();
      if (now !== result._calcDate) {
        const calc = calculateInternal({ filingMatrix });
        calc._calcDate = now;
        setResult(calc);
      }
    }, 60000);
    return () => clearInterval(check);
  }, [result, filingMatrix]);

  // Calculate
  const handleCalculate = () => {
    const calc = calculateInternal({ filingMatrix });
    calc._calcDate = new Date().toDateString();
    setResult(calc);
    autoSave('calculation_result', calc);
    if (!calc.empty) {
      autoSave('worksheet_status', 'completed');
    }
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
  };

  // Convert to lead — requires CIN, then reset to list
  const convertToLead = async () => {
    if (!activeWs) return;
    if (!cinValue.trim()) { setCinError('CIN is required'); return; }
    if (!/^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(cinValue.trim().toUpperCase())) { setCinError('Invalid CIN format. Expected: L/U + 5 digits + 2 letters (state) + 4 digits (year) + 3 letters (type) + 6 digits'); return; }
    setCinError('');
    try {
      const res = await fetch(`${API_URL}/api/worksheets/${activeWs.id}/convert-to-lead`, {
        method: 'POST', headers, body: JSON.stringify({ cin: cinValue.trim().toUpperCase() })
      });
      if (res.ok) {
        toast.success('Lead created and added to CCFS Leads. Start a new worksheet anytime.');
        setActiveWs(null); setShowCinModal(false); setCinValue('');
        setStatusField(''); setCessationFY(''); setIntent(''); setLastFiledFY('');
        setFilingMatrix({}); setResult(null); setCompanyName('');
        setShowList(true);
        loadWorksheets();
      }
    } catch { toast.error('Failed to create lead'); }
  };

  // Save recalculated results to an existing lead
  const saveToLead = async () => {
    if (!recalcLead || !result || result.empty) return;
    setSaving(true);
    try {
      const body = {
        calculation_result: result,
        quiz_answers: [statusField, cessationFY || "", intent],
        filing_matrix: filingMatrix,
        cessation_fy: cessationFY || "",
        assumed_filing_fy: isNotSure ? (assumedFilingFY || "") : "",
        is_not_sure: isNotSure,
      };
      const res = await fetch(`${API_URL}/api/ccfs-leads/${recalcLead.id}/recalculate`, {
        method: 'PATCH', headers, body: JSON.stringify(body)
      });
      if (res.ok) {
        setSavedToLead(true);
        toast.success(`Savings updated for ${recalcLead.lead_number}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save');
      }
    } catch { toast.error('Failed to save to lead'); }
    finally { setSaving(false); }
  };


  // Delete worksheet
  const deleteWorksheet = async (id, e) => {
    e.stopPropagation();
    if (!confirm('Delete this worksheet?')) return;
    try {
      await fetch(`${API_URL}/api/worksheets/${id}`, { method: 'DELETE', headers });
      setWorksheets(prev => prev.filter(w => w.id !== id));
      if (activeWs?.id === id) { setActiveWs(null); setShowList(true); }
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  // Can calculate
  const canCalc = statusField && intent && (Object.keys(filingMatrix).length > 0 || (isNotSure && assumedFilingFY));
  const readOnly = activeWs?.worksheet_status === 'converted';
  const isActivePartnerWs = isAdmin && activeWs?.source === 'r-pace';

  // Matrix FYs to show
  const matrixFYs = Object.keys(filingMatrix).sort();

  const statusBadge = (s) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-600',
      in_progress: 'bg-amber-50 text-amber-700',
      completed: 'bg-green-50 text-green-700',
      converted: 'bg-blue-50 text-blue-700',
    };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors[s] || colors.draft}`}>{(s || 'draft').replace('_', ' ')}</span>;
  };

  // ─── Admin grouping helpers ───
  const isPartnerWs = (ws) => ws.source === 'r-pace';
  const getGroup = (ws) => {
    if (ws.source !== 'r-pace') return 'admin';
    return (ws.created_by_role || '').toUpperCase() === 'CS' ? 'cs' : 'ca';
  };

  const getDaysOld = (ws) => {
    if (!ws.created_at) return 0;
    return Math.floor((Date.now() - new Date(ws.created_at).getTime()) / 86400000);
  };

  const getAgeingBucket = (days) => {
    if (days <= 1) return '1d';
    if (days <= 3) return '3d';
    if (days <= 5) return '5d';
    if (days <= 7) return '7d';
    return '7d+';
  };

  // Compute ageing stats for unconverted worksheets
  const ageingStats = useMemo(() => {
    const unconverted = worksheets.filter(ws => ws.worksheet_status !== 'converted');
    const buckets = { '1d': [], '3d': [], '5d': [], '7d': [], '7d+': [] };
    unconverted.forEach(ws => {
      const days = getDaysOld(ws);
      buckets[getAgeingBucket(days)].push(ws);
    });
    return buckets;
  }, [worksheets]);

  // Filter + sort worksheets for display
  const displayWorksheets = useMemo(() => {
    let filtered = [...worksheets];

    // Source filter
    if (isAdmin && sourceFilter !== 'all') {
      filtered = filtered.filter(ws => getGroup(ws) === sourceFilter);
    }

    // Ageing filter (only unconverted)
    if (ageingFilter) {
      filtered = filtered.filter(ws => {
        if (ws.worksheet_status === 'converted') return false;
        return getAgeingBucket(getDaysOld(ws)) === ageingFilter;
      });
    }

    // Sort
    if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
    } else if (sortBy === 'ageing') {
      filtered.sort((a, b) => getDaysOld(b) - getDaysOld(a));
    } else if (sortBy === 'status') {
      const order = { draft: 0, in_progress: 1, completed: 2, converted: 3 };
      filtered.sort((a, b) => (order[a.worksheet_status] || 0) - (order[b.worksheet_status] || 0));
    }

    return filtered;
  }, [worksheets, sourceFilter, ageingFilter, sortBy, isAdmin]);

  // Group worksheets by source for admin
  const groupedWorksheets = useMemo(() => {
    if (!isAdmin) return null;
    const groups = { admin: [], ca: [], cs: [] };
    displayWorksheets.forEach(ws => {
      groups[getGroup(ws)].push(ws);
    });
    return groups;
  }, [displayWorksheets, isAdmin]);

  // Status counts for a list of worksheets
  const statusCounts = (list) => {
    const counts = { draft: 0, in_progress: 0, completed: 0, converted: 0 };
    list.forEach(ws => { counts[ws.worksheet_status || 'draft']++; });
    return counts;
  };

  const toggleGroup = (group) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // ─── Reusable worksheet card ───
  const WorksheetCard = ({ ws, canDelete }) => (
    <Card key={ws.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openWorksheet(ws)} data-testid={`worksheet-${ws.id}`}>
      <CardContent className="p-4 flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="font-semibold text-sm text-slate-800 truncate">{ws.company_name}</p>
            {statusBadge(ws.worksheet_status)}
            {isAdmin && isPartnerWs(ws) && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getGroup(ws) === 'ca' ? 'bg-indigo-100 text-indigo-700' : 'bg-purple-100 text-purple-700'}`}>
                {ws.created_by_name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>Updated {new Date(ws.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
            {ws.worksheet_status !== 'converted' && (
              <span className={`${getDaysOld(ws) > 7 ? 'text-red-400' : getDaysOld(ws) > 3 ? 'text-amber-400' : 'text-slate-400'}`}>
                · {getDaysOld(ws)}d old
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {ws.calculation_result?.totalSaving && (
            <span className="text-xs font-semibold text-green-600">{fmt(ws.calculation_result.totalSaving)} saved</span>
          )}
          {canDelete && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={(e) => deleteWorksheet(ws.id, e)} data-testid={`delete-ws-${ws.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
          <ArrowRight className="w-4 h-4 text-slate-300" />
        </div>
      </CardContent>
    </Card>
  );

  // ─── Group section header ───
  const GroupHeader = ({ label, color, list, groupKey }) => {
    const counts = statusCounts(list);
    const isCollapsed = collapsedGroups[groupKey];
    return (
      <button
        onClick={() => toggleGroup(groupKey)}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg ${color} transition-colors`}
        data-testid={`group-header-${groupKey}`}
      >
        <div className="flex items-center gap-2">
          {isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          <span className="text-sm font-bold">{label}</span>
          <span className="text-xs font-medium opacity-70">({list.length})</span>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-medium">
          {counts.draft > 0 && <span className="bg-white/60 px-1.5 py-0.5 rounded">Draft: {counts.draft}</span>}
          {counts.in_progress > 0 && <span className="bg-white/60 px-1.5 py-0.5 rounded">In Progress: {counts.in_progress}</span>}
          {counts.completed > 0 && <span className="bg-white/60 px-1.5 py-0.5 rounded">Completed: {counts.completed}</span>}
          {counts.converted > 0 && <span className="bg-white/60 px-1.5 py-0.5 rounded">Converted: {counts.converted}</span>}
        </div>
      </button>
    );
  };

  // ─── LIST VIEW ───
  if (showList) {
    return (
      <div className="space-y-4" data-testid="internal-calc-list">
        {recalcLead && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between" data-testid="recalc-banner">
            <div>
              <p className="text-sm font-semibold text-amber-800">Re-calculating for: {recalcLead.company_name}</p>
              <p className="text-xs text-amber-600">Lead {recalcLead.lead_number} {recalcLead.cin ? `· ${recalcLead.cin}` : ''}. Create a new worksheet below, fill Q1-Q3 based on MCA data, calculate, then save to lead.</p>
            </div>
          </div>
        )}

        {/* New worksheet input — always at top */}
        <div className="flex items-center gap-3">
          <Input
            placeholder="Enter company name to start..."
            value={newCompany}
            onChange={e => setNewCompany(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createWorksheet()}
            className="flex-1"
            data-testid="new-worksheet-company"
          />
          <Button onClick={createWorksheet} className="bg-[#1e3a5f] hover:bg-[#152d4a] gap-2 shrink-0" data-testid="create-worksheet-btn">
            <Calculator className="w-4 h-4" /> New Worksheet
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Loading worksheets...</p>
        ) : worksheets.length === 0 ? (
          <Card><CardContent className="py-12 text-center">
            <Calculator className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No worksheets yet. Enter a company name above to start.</p>
          </CardContent></Card>
        ) : isAdmin ? (
          /* ─── ADMIN: Grouped View with Filters ─── */
          <div className="space-y-4">
            {/* Ageing Stats Bar */}
            <Card data-testid="ageing-stats-bar">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Ageing — Unconverted Worksheets</span>
                  {ageingFilter && (
                    <button onClick={() => setAgeingFilter(null)} className="text-[10px] text-red-500 hover:underline ml-auto">Clear filter</button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { key: '1d', label: '≤1 day', color: 'bg-green-50 text-green-700 border-green-200' },
                    { key: '3d', label: '≤3 days', color: 'bg-lime-50 text-lime-700 border-lime-200' },
                    { key: '5d', label: '≤5 days', color: 'bg-amber-50 text-amber-700 border-amber-200' },
                    { key: '7d', label: '≤7 days', color: 'bg-orange-50 text-orange-700 border-orange-200' },
                    { key: '7d+', label: '>7 days', color: 'bg-red-50 text-red-700 border-red-200' },
                  ].map(b => (
                    <button
                      key={b.key}
                      onClick={() => setAgeingFilter(ageingFilter === b.key ? null : b.key)}
                      className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-all ${
                        ageingFilter === b.key ? 'ring-2 ring-offset-1 ring-slate-400 shadow-sm' : ''
                      } ${ageingStats[b.key].length > 0 ? b.color + ' cursor-pointer hover:shadow-sm' : 'bg-slate-50 text-slate-300 border-slate-100 cursor-default'}`}
                      disabled={ageingStats[b.key].length === 0}
                      data-testid={`ageing-${b.key}`}
                    >
                      {b.label}: {ageingStats[b.key].length}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Filter Bar + Sort */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1.5" data-testid="source-filter">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'admin', label: 'Admin' },
                  { key: 'ca', label: 'CA' },
                  { key: 'cs', label: 'CS' },
                ].map(f => (
                  <button
                    key={f.key}
                    onClick={() => { setSourceFilter(f.key); setAgeingFilter(null); }}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${
                      sourceFilter === f.key
                        ? 'bg-[#1e3a5f] text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`filter-${f.key}`}
                  >
                    {f.label}
                    {f.key === 'all' && ` (${worksheets.length})`}
                    {f.key === 'admin' && ` (${groupedWorksheets?.admin.length || 0})`}
                    {f.key === 'ca' && ` (${groupedWorksheets?.ca.length || 0})`}
                    {f.key === 'cs' && ` (${groupedWorksheets?.cs.length || 0})`}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5" data-testid="sort-options">
                <SortAsc className="w-3.5 h-3.5 text-slate-400" />
                {[
                  { key: 'newest', label: 'Newest' },
                  { key: 'ageing', label: 'Oldest' },
                  { key: 'status', label: 'Status' },
                ].map(s => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`text-[10px] font-medium px-2 py-1 rounded transition-all ${
                      sortBy === s.key ? 'bg-slate-200 text-slate-800' : 'text-slate-400 hover:text-slate-600'
                    }`}
                    data-testid={`sort-${s.key}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Grouped or Filtered List */}
            {sourceFilter === 'all' && !ageingFilter ? (
              /* Show grouped sections */
              <div className="space-y-3">
                {/* Admin group */}
                {groupedWorksheets.admin.length > 0 && (
                  <div data-testid="group-admin">
                    <GroupHeader label="Admin Worksheets" color="bg-slate-100 text-slate-700 hover:bg-slate-200" list={groupedWorksheets.admin} groupKey="admin" />
                    {!collapsedGroups.admin && (
                      <div className="space-y-2 mt-2 pl-1">
                        {groupedWorksheets.admin.map(ws => <WorksheetCard key={ws.id} ws={ws} canDelete={true} />)}
                      </div>
                    )}
                  </div>
                )}

                {/* CA group */}
                {groupedWorksheets.ca.length > 0 && (
                  <div data-testid="group-ca">
                    <GroupHeader label="CA Partner Worksheets" color="bg-indigo-50 text-indigo-700 hover:bg-indigo-100" list={groupedWorksheets.ca} groupKey="ca" />
                    {!collapsedGroups.ca && (
                      <div className="space-y-2 mt-2 pl-1">
                        {groupedWorksheets.ca.map(ws => <WorksheetCard key={ws.id} ws={ws} canDelete={false} />)}
                      </div>
                    )}
                  </div>
                )}

                {/* CS group */}
                {groupedWorksheets.cs.length > 0 && (
                  <div data-testid="group-cs">
                    <GroupHeader label="CS Partner Worksheets" color="bg-purple-50 text-purple-700 hover:bg-purple-100" list={groupedWorksheets.cs} groupKey="cs" />
                    {!collapsedGroups.cs && (
                      <div className="space-y-2 mt-2 pl-1">
                        {groupedWorksheets.cs.map(ws => <WorksheetCard key={ws.id} ws={ws} canDelete={false} />)}
                      </div>
                    )}
                  </div>
                )}

                {displayWorksheets.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-6">No worksheets match the current filters.</p>
                )}
              </div>
            ) : (
              /* Flat filtered list */
              <div className="space-y-2">
                {displayWorksheets.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-6">No worksheets match the current filters.</p>
                ) : (
                  displayWorksheets.map(ws => (
                    <WorksheetCard key={ws.id} ws={ws} canDelete={!isPartnerWs(ws)} />
                  ))
                )}
              </div>
            )}
          </div>
        ) : (
          /* ─── PARTNER: Simple flat list (unchanged) ─── */
          <div className="space-y-2">
            {worksheets.map(ws => (
              <Card key={ws.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openWorksheet(ws)} data-testid={`worksheet-${ws.id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-sm text-slate-800 truncate">{ws.company_name}</p>
                      {statusBadge(ws.worksheet_status)}
                    </div>
                    <p className="text-xs text-slate-400">
                      Updated {new Date(ws.updated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {ws.calculation_result?.totalSaving && (
                      <span className="text-xs font-semibold text-green-600">{fmt(ws.calculation_result.totalSaving)} saved</span>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={(e) => deleteWorksheet(ws.id, e)} data-testid={`delete-ws-${ws.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                    <ArrowRight className="w-4 h-4 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ─── CALCULATOR VIEW ───
  return (
    <div className="space-y-4" data-testid="internal-calc-view">
      {/* Recalc banner */}
      {recalcLead && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="recalc-banner-calc">
          <p className="text-sm font-semibold text-amber-800">Re-calculating for: {recalcLead.company_name} ({recalcLead.lead_number})</p>
          <p className="text-xs text-amber-600 mt-0.5">Fill Q1-Q3 based on MCA data, calculate, then click "Save to Lead" to update the lead's savings.</p>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <button onClick={() => setShowList(true)} className="text-sm text-[#1e3a5f] font-medium hover:underline" data-testid="back-to-list">
          &larr; All Worksheets
        </button>
        <div className="flex items-center gap-2">
          {saving && <span className="text-[10px] text-slate-400">Saving...</span>}
          {isActivePartnerWs && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-200" data-testid="partner-ws-badge">
              {activeWs.created_by_name} ({activeWs.created_by_role}) — View Only
            </span>
          )}
          {statusBadge(activeWs?.worksheet_status)}
        </div>
      </div>

      {/* Company Name */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Company</p>
          <p className="text-lg font-bold text-[#1e3a5f]" data-testid="ws-company-name">{companyName}</p>
        </CardContent>
      </Card>

      {/* Q1: Company Status */}
      <Card className={readOnly ? 'opacity-80' : ''}>
        <CardContent className="p-4 space-y-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Q1: Company Status</p>
          <p className="text-xs text-slate-400 mb-1">Select the option that best describes your current situation</p>
          <div className="flex gap-2">
            {[['running', 'Business is running'], ['closed', 'Business has stopped']].map(([val, label]) => (
              <button key={val} onClick={() => !readOnly && handleStatus(val)} disabled={readOnly}
                className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${readOnly ? 'cursor-default' : ''} ${statusField === val ? 'bg-[#1e3a5f] text-white shadow-md ring-2 ring-[#1e3a5f]/30' : 'bg-white border border-slate-200 text-slate-600' + (readOnly ? '' : ' hover:border-slate-400 hover:shadow-sm')}`}
                data-testid={`int-status-${val}`}
              >{label}</button>
            ))}
          </div>
          {statusField === 'closed' && (
            <div className="pl-4 border-l-2 border-[#1e3a5f]/20 space-y-3 pt-2" ref={cessationRef}>
              <div>
                <p className="text-sm font-medium text-slate-700 mb-1">When did business activity stop?</p>
                <p className="text-xs text-slate-400 mb-3">Not sure of the exact year? Select your best estimate. Our team cross-checks with MCA records before any action is taken.</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {CESSATION_OPTIONS.map(fy => (
                  <button key={fy} onClick={() => !readOnly && handleCessation(fy)} disabled={readOnly}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${readOnly ? 'cursor-default' : ''} ${cessationFY === fy ? 'bg-[#1e3a5f] text-white shadow-md ring-2 ring-[#1e3a5f]/30' : 'bg-white border border-slate-200 text-slate-600' + (readOnly ? '' : ' hover:border-slate-400 hover:shadow-sm')}`}
                    data-testid={`int-cessation-${fy.replace(/\s/g, '-')}`}
                  >{fy}</button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Q2: Intent */}
      {statusField && (
        <Card className={readOnly ? 'opacity-80' : ''} ref={q2Ref}>
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Q2: What do you want to do?</p>
            <p className="text-xs text-slate-400 mb-1">This determines the scope of filings needed and the overall compliance path.</p>
            <div className="flex gap-2">
              {[['close', 'Close (Strike-off)'], ['dormant', 'Make Dormant'], ['continue', 'Continue / Regularise']].map(([val, label]) => (
                <button key={val} onClick={() => !readOnly && handleIntent(val)} disabled={readOnly}
                  className={`flex-1 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${readOnly ? 'cursor-default' : ''} ${intent === val ? 'bg-[#1e3a5f] text-white shadow-md ring-2 ring-[#1e3a5f]/30' : 'bg-white border border-slate-200 text-slate-600' + (readOnly ? '' : ' hover:border-slate-400 hover:shadow-sm')}`}
                  data-testid={`int-intent-${val}`}
                >{label}</button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Q3: Filing Status */}
      {intent && (
        <Card className={readOnly ? 'opacity-80' : ''} ref={q3Ref}>
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Q3: Filing Status</p>
            <div>
              <p className="text-sm font-medium text-slate-700 mb-1">Till which year were both FS & AR last filed?</p>
              <p className="text-xs text-slate-400 mb-3">Select the last financial year where both Balance Sheet (AOC-4) and Annual Return (MGT-7) were filed with ROC.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {LAST_FILED_OPTIONS.map(fy => (
                <button key={fy} onClick={() => !readOnly && handleLastFiled(fy)} disabled={readOnly}
                  className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${readOnly ? 'cursor-default' : ''} ${lastFiledFY === fy && !isNotSure ? 'bg-[#1e3a5f] text-white shadow-md ring-2 ring-[#1e3a5f]/30' : 'bg-white border border-slate-200 text-slate-600' + (readOnly ? '' : ' hover:border-slate-400 hover:shadow-sm')}`}
                  data-testid={`int-lastfiled-${fy.replace(/\s/g, '-')}`}
                >{fy}</button>
              ))}
            </div>
            {/* "Not sure" option */}
            <button onClick={() => !readOnly && handleNotSure()} disabled={readOnly}
              className={`w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-center gap-2 ${readOnly ? 'cursor-default' : ''} ${isNotSure ? 'bg-[#1e3a5f] text-white shadow-md ring-2 ring-[#1e3a5f]/30' : 'bg-white border border-slate-200 text-slate-600' + (readOnly ? '' : ' hover:border-slate-400 hover:shadow-sm')}`}
              data-testid="int-lastfiled-noidea"
            >
              <HelpCircle className="w-4 h-4" /> Don't remember / Not sure about filings
            </button>

            {/* Assumed FY info */}
            {isNotSure && assumedFilingFY && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-800">We'll estimate based on: <span className="font-bold">{assumedFilingFY}</span></p>
                <p className="text-xs text-blue-600 mt-0.5">
                  {statusField === 'closed' ? 'Based on your business stop year.' : 'Estimated as 2 years prior to current year.'}
                  {' '}You can adjust individual filings in the matrix below.
                </p>
              </div>
            )}

            {matrixFYs.length > 0 && (
              <div className="mt-3">
                <p className="text-xs text-slate-500 mb-2">{readOnly ? 'Filing status at time of conversion' : 'Adjust individual filings — '}<span className="text-slate-400">{readOnly ? '' : 'uncheck if NOT filed for that year'}</span></p>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left px-3 py-2 text-xs font-semibold text-slate-500">FY</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">FS (AOC-4)</th>
                        <th className="text-center px-3 py-2 text-xs font-semibold text-slate-500">AR (MGT-7)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {matrixFYs.map(fy => {
                        const entry = filingMatrix[fy] || {};
                        return (
                          <tr key={fy} className="border-b last:border-0 hover:bg-slate-50/50">
                            <td className="px-3 py-2 font-medium text-slate-700">FY {fy}</td>
                            <td className="text-center px-3 py-2">
                              <div className="flex items-center justify-center gap-1.5">
                                <Checkbox
                                  checked={entry.fs}
                                  onCheckedChange={() => !readOnly && toggleCell(fy, 'fs')}
                                  disabled={readOnly}
                                  data-testid={`matrix-fs-${fy}`}
                                />
                                <span className={`text-xs ${entry.fs ? 'text-green-600' : 'text-red-500 font-semibold'}`}>
                                  {entry.fs ? 'Filed' : 'Not Filed'}
                                </span>
                              </div>
                            </td>
                            <td className="text-center px-3 py-2">
                              <div className="flex items-center justify-center gap-1.5">
                                <Checkbox
                                  checked={entry.ar}
                                  onCheckedChange={() => !readOnly && toggleCell(fy, 'ar')}
                                  disabled={readOnly}
                                  data-testid={`matrix-ar-${fy}`}
                                />
                                <span className={`text-xs ${entry.ar ? 'text-green-600' : 'text-red-500 font-semibold'}`}>
                                  {entry.ar ? 'Filed' : 'Not Filed'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Calculate Button — hidden for converted worksheets */}
      {canCalc && !readOnly && (
        <div ref={calcBtnRef}>
          <Button onClick={handleCalculate} className="w-full bg-[#1e3a5f] hover:bg-[#152d4a] py-5 gap-2 text-sm" data-testid="int-calculate-btn">
            <Calculator className="w-4 h-4" /> Calculate Penalties & Savings
          </Button>
        </div>
      )}

      {/* Results */}
      {result && !result.empty && (
        <Card className="border-green-200 bg-green-50/30" ref={resultRef}>
          <CardContent className="p-4 space-y-4" data-testid="int-results">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-white rounded-lg p-3 border">
                <p className="text-[10px] text-slate-500 uppercase mb-1">Normal Fee</p>
                <p className="text-lg font-bold text-slate-800">{fmt(result.totalNormal)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-green-200">
                <p className="text-[10px] text-green-600 uppercase mb-1">CCFS Fee</p>
                <p className="text-lg font-bold text-green-700">{fmt(result.totalCCFS)}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-green-300">
                <p className="text-[10px] text-green-600 uppercase mb-1">Total Saving</p>
                <p className="text-lg font-bold text-green-700">{fmt(result.totalSaving)}</p>
              </div>
            </div>

            {/* Detailed Table */}
            <div className="border rounded-lg overflow-x-auto bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1e3a5f] text-white">
                    <th className="text-left px-3 py-2 text-xs">FY</th>
                    <th className="text-right px-3 py-2 text-xs">FS Delay</th>
                    <th className="text-right px-3 py-2 text-xs">AR Delay</th>
                    <th className="text-right px-3 py-2 text-xs">Total Delay</th>
                    <th className="text-right px-3 py-2 text-xs">Normal Fee</th>
                    <th className="text-right px-3 py-2 text-xs">CCFS Fee</th>
                    <th className="text-right px-3 py-2 text-xs">Saving</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.fyDetails || []).map((d, i) => (
                    <tr key={d.fy} className={`border-b last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                      <td className="px-3 py-2 font-medium text-slate-700">FY {d.fy}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{d.fsDelay > 0 ? `${d.fsDelay} days` : '—'}</td>
                      <td className="px-3 py-2 text-right text-slate-600">{d.arDelay > 0 ? `${d.arDelay} days` : '—'}</td>
                      <td className="px-3 py-2 text-right font-medium text-slate-700">{d.totalDelay} days</td>
                      <td className="px-3 py-2 text-right text-slate-600">{fmt(d.normalFee)}</td>
                      <td className="px-3 py-2 text-right text-green-600">{fmt(d.ccfsFee)}</td>
                      <td className="px-3 py-2 text-right font-semibold text-green-700">{fmt(d.saving)}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-100 font-bold">
                    <td className="px-3 py-2 text-slate-800">Total</td>
                    <td className="px-3 py-2" colSpan={3}></td>
                    <td className="px-3 py-2 text-right text-slate-800">{fmt(result.totalNormal)}</td>
                    <td className="px-3 py-2 text-right text-green-700">{fmt(result.totalCCFS)}</td>
                    <td className="px-3 py-2 text-right text-green-700">{fmt(result.totalSaving)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Actions */}
            {recalcLead && !savedToLead && (
              <Button onClick={saveToLead} className="w-full bg-amber-600 hover:bg-amber-700 gap-2 py-5" disabled={saving} data-testid="save-to-lead-btn">
                {saving ? <Save className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Save to Lead {recalcLead.lead_number}
              </Button>
            )}
            {recalcLead && savedToLead && (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-sm text-green-600 py-2" data-testid="saved-to-lead-badge">
                  <Check className="w-4 h-4" /> Savings updated for {recalcLead.lead_number}
                </div>
                <Button variant="outline" className="w-full border-[#1e3a5f] text-[#1e3a5f] gap-2" onClick={() => navigate('/admin/ccfs-leads')} data-testid="back-to-leads-btn">
                  <ArrowRight className="w-4 h-4" /> Back to CCFS Leads
                </Button>
              </div>
            )}
            {!recalcLead && !readOnly && !isActivePartnerWs && (
              <Button onClick={() => { setShowCinModal(true); setCinError(''); }} variant="outline" className="w-full border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f]/5 gap-2" data-testid="convert-to-lead-btn">
                <FileText className="w-4 h-4" /> Save as CCFS Lead
              </Button>
            )}
            {readOnly && (
              <div className="flex items-center justify-center gap-2 text-sm text-blue-600 py-2" data-testid="converted-badge">
                <Check className="w-4 h-4" /> Lead created from this worksheet (read-only)
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {result?.empty && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardContent className="p-4 text-center text-sm text-amber-700">
            No pending filings found. All selected years appear to be filed.
          </CardContent>
        </Card>
      )}

      {/* CIN Modal */}
      {showCinModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCinModal(false)}>
          <Card className="w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <CardContent className="p-6 space-y-4">
              <div>
                <p className="text-sm font-semibold text-[#1e3a5f] mb-1">Save as CCFS Lead</p>
                <p className="text-xs text-slate-500">Enter the CIN to create a lead for <span className="font-medium text-slate-700">{companyName}</span></p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">CIN (Corporate Identity Number)</label>
                <Input
                  value={cinValue}
                  onChange={e => { setCinValue(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 21)); setCinError(''); }}
                  placeholder="e.g. U12345MH2020PTC123456"
                  maxLength={21}
                  className={`h-11 font-mono tracking-wide ${cinError ? 'border-red-400' : ''}`}
                  data-testid="cin-modal-input"
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && convertToLead()}
                />
                {cinError && <p className="text-red-500 text-xs mt-1">{cinError}</p>}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowCinModal(false)} data-testid="cin-modal-cancel">Cancel</Button>
                <Button className="flex-1 bg-[#1e3a5f] hover:bg-[#152d4a] gap-2" onClick={convertToLead} data-testid="cin-modal-submit">
                  <FileText className="w-4 h-4" /> Create Lead
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
